import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { ShiftListPane } from '@apps/web/components/dashboard/shift_list_pane';
import {
    resolveShiftsPresentation,
    type ShiftsPresentation,
} from '@apps/web/components/dashboard/shifts_presentation';
import { ShiftsPresentationToggle } from '@apps/web/components/dashboard/shifts_presentation_toggle';
import { TrainingModePageTourScope } from '@apps/web/components/page_tour/training_mode_page_tour';
import { SHIFTS_PAGE_TOUR_STEPS } from '@apps/web/components/page_tour/training_mode_tour_configs';
import { type CalendarViewMode } from '@apps/web/components/shifts_calendar/calendar_model';
import { ShiftCalendarPane } from '@apps/web/components/shifts_calendar/shift_calendar_pane';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import {
    createAgentChatDestinationResolver,
    normalizeAgentChatSearch,
} from '@apps/web/lib/agent_chat_navigation';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const shiftsSearchSchema = z.object({
    date: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    status: z.enum(['open', 'needs_attention']).optional(),
    clientIds: z.array(z.string().uuid()).optional(),
    shiftId: z.string().optional(),
    approve: z.boolean().optional(),
    calloutShiftId: z.string().optional(),
    chatId: z.string().uuid().optional(),
    chatMode: z.enum(['fullscreen']).optional(),
    slackWebClaim: z.string().optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
    // v3 only. The page hosts both presentations; `/calendar` redirects here.
    presentation: z.enum(['calendar', 'table']).optional(),
    // Calendar state, kept on this route so a Calendar → Table → Calendar
    // round trip does not lose the chosen view mode or person scope.
    view: z.enum(['day', 'week', 'month']).optional(),
    scope: z.string().optional(),
});

const resolveAgentChatDestination = createAgentChatDestinationResolver();

export async function handleShiftsBeforeLoad({
    search,
}: {
    readonly search: z.infer<typeof shiftsSearchSchema>;
}): Promise<void> {
    if (
        search.chatId === undefined ||
        search.organizationId === undefined ||
        search.mode === undefined ||
        search.slackWebClaim !== undefined
    ) {
        return;
    }

    const resolution = await resolveAgentChatDestination({
        chatId: search.chatId,
        organizationId: search.organizationId,
        mode: search.mode,
    });
    if (
        resolution.kind === 'stale' ||
        resolution.destination.kind === 'panel'
    ) {
        return;
    }

    const normalizedSearch = normalizeAgentChatSearch(search);
    if (
        normalizedSearch.organizationId === undefined ||
        normalizedSearch.mode === undefined
    ) {
        return;
    }
    throw redirect({
        to: '/agent/runs/$runId',
        params: { runId: resolution.destination.runId },
        search: {
            organizationId: normalizedSearch.organizationId,
            mode: normalizedSearch.mode,
        },
        replace: true,
    });
}

export const Route = createFileRoute('/_app/(main)/shifts/')({
    component: ShiftsPage,
    validateSearch: shiftsSearchSchema,
    beforeLoad: handleShiftsBeforeLoad,
    head: () => ({
        meta: [
            {
                title: 'Shifts | Phoebe',
            },
        ],
    }),
});

function ShiftsPage() {
    const navigate = useNavigate();
    const currentSearch = Route.useSearch();
    const {
        date,
        from,
        to,
        status,
        clientIds,
        shiftId,
        approve,
        calloutShiftId,
        presentation,
        view,
        scope,
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
    } = currentSearch;
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );
    const organizationId = useOrganizationId();
    const { data: organization, isLoading: isLoadingOrganization } =
        useOrganization(organizationId, { reason: 'shifts-page' });
    const agentV3Enabled = useFeatureFlag(
        'agent_v3_enabled',
        organization?.features,
    );

    // Which presentation this page renders depends on the organization's
    // feature flags, so it cannot be decided before they arrive. Rendering
    // early would mount the table, fire its shift query, and then throw both
    // away when v3 resolves and the calendar takes over. SWR reports
    // `isLoading` false for an organization-less session, so this does not
    // strand a user without one.
    if (isWaitingForDeepLinkOrganization || isLoadingOrganization) {
        return null;
    }

    const resolvedPresentation = resolveShiftsPresentation({
        presentation,
        agentV3Enabled,
        hasShiftDeepLink:
            shiftId !== undefined ||
            approve !== undefined ||
            calloutShiftId !== undefined,
    });

    function handlePresentationChange(next: ShiftsPresentation): void {
        void navigate({
            to: '/shifts',
            search: { ...currentSearch, presentation: next },
            replace: true,
        });
    }

    function handleCalendarRangeChange(from: string, to: string): void {
        void navigate({
            to: '/shifts',
            search: { ...currentSearch, from, to },
            replace: true,
        });
    }

    function handleCalendarViewChange(
        view: CalendarViewMode,
        from: string,
        to: string,
    ): void {
        void navigate({
            to: '/shifts',
            search: { ...currentSearch, view, from, to },
        });
    }

    function handleCalendarScopeChange(
        scope: string | null,
        options?: { readonly replace?: boolean },
    ): void {
        void navigate({
            to: '/shifts',
            search: { ...currentSearch, scope: scope ?? undefined },
            replace: options?.replace ?? false,
        });
    }

    // Only v3 organizations get the switcher; everyone else sees the table
    // exactly as before.
    const presentationToggle = agentV3Enabled ? (
        <ShiftsPresentationToggle
            value={resolvedPresentation}
            onChange={handlePresentationChange}
        />
    ) : undefined;

    if (resolvedPresentation === 'calendar') {
        return (
            <div className="flex h-full min-h-0 flex-col overflow-hidden">
                <div className="min-h-0 flex-1 max-md:[&>div:first-child]:!h-full">
                    <ShiftCalendarPane
                        initialFrom={from}
                        initialTo={to}
                        view={view ?? 'week'}
                        viewExplicit={view !== undefined}
                        scope={scope}
                        presentationToggle={presentationToggle}
                        unifiedHeader={true}
                        onRangeChange={handleCalendarRangeChange}
                        onViewChange={handleCalendarViewChange}
                        onScopeChange={handleCalendarScopeChange}
                    />
                </div>
            </div>
        );
    }

    return (
        <TrainingModePageTourScope
            pageId="shifts"
            organizationId={organizationId}
            ready={organizationId !== null}
            steps={SHIFTS_PAGE_TOUR_STEPS}
        >
            <ShiftListPane
                initialDate={date}
                initialFrom={from}
                initialTo={to}
                initialStatus={status}
                initialClientIds={clientIds}
                selectedShiftId={shiftId}
                showApproveModal={approve}
                calloutShiftId={calloutShiftId}
                presentationToggle={presentationToggle}
                unifiedHeader={agentV3Enabled}
            />
        </TrainingModePageTourScope>
    );
}
